// ── leeches.js — пиявки и вопросы для игр ───────────────────────────────────
// Пиявка (как в Anki) — карточка, которую забывали снова и снова: srs_lapses ≥ LEECH_LAPSES.
// Её «обрабатывают» в играх: два верных ответа — и пиявка снята, пока её снова не забудут
// в повторении (тогда srs_lapses вырастет и она станет пиявкой опять). Игры расписание FSRS
// не трогают — это закрепление, а не повторение.
//
// Что обработано, хранится в ac_leech_done: { [id карточки]: { l: забываний тогда, h: попаданий } }
// и синхронизируется между устройствами (settings.js).

import { markSetting } from '../settings';

export const LEECH_LAPSES = 4; // с какого числа забываний карточка — пиявка
export const LEECH_PROMPT_AT = 10; // столько пиявок — предлагаем поиграть
export const HITS_TO_CLEAR = 2; // верных ответов в играх, чтобы снять пиявку
const DONE_KEY = 'ac_leech_done';

const readDone = () => {
  try { return JSON.parse(localStorage.getItem(DONE_KEY) || '{}') || {}; } catch { return {}; }
};
const lapsesOf = (card) => Number(card?.srs_lapses) || 0;

export function isLeech(card, done = readDone()) {
  const l = lapsesOf(card);
  if (l < LEECH_LAPSES) return false;
  const d = done[card.id];
  return !(d && d.l === l && d.h >= HITS_TO_CLEAR);
}

export const leechesOf = (cards, done = readDone()) => cards.filter((c) => isLeech(c, done));

// Верный ответ в игре. Для пиявки засчитывается попадание; вернёт true, если пиявка
// этим ответом снята.
export function recordGameHit(card) {
  const l = lapsesOf(card);
  if (l < LEECH_LAPSES || !card?.id) return false;
  const done = readDone();
  const prev = done[card.id];
  const d = prev && prev.l === l ? { ...prev } : { l, h: 0 };
  d.h += 1;
  done[card.id] = d;
  try { localStorage.setItem(DONE_KEY, JSON.stringify(done)); markSetting(DONE_KEY); } catch { /* нет хранилища */ }
  return d.h === HITS_TO_CLEAR;
}

// Для игр годятся карточки со словом и переводом, не слишком длинные — слово летит по экрану.
// Меньше MIN_GAME_CARDS таких — играть не из чего (нужны неверные варианты).
export const MIN_GAME_CARDS = 4;
export const MAX_GAME_WORD = 28;
export const playable = (cards) => cards.filter((c) => {
  const w = String(c?.word || '').trim();
  return w && w.length <= MAX_GAME_WORD && String(c?.translation || '').trim();
});

export function shuffle(arr, rnd = Math.random) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Набор карточек на одну игру: n штук, в случайном порядке, все слова разные (у двух
// карточек бывает одно и то же слово — в игре они выглядели бы одинаково и путали).
export function pickRound(cards, n = 10, rnd = Math.random) {
  const seen = new Set();
  const out = [];
  for (const c of shuffle(cards, rnd)) {
    const w = String(c.word).trim().toLowerCase();
    const tr = String(c.translation).trim().toLowerCase();
    if (seen.has(w) || seen.has(`t:${tr}`)) continue;
    seen.add(w);
    seen.add(`t:${tr}`);
    out.push(c);
    if (out.length >= n) break;
  }
  return out;
}

// Для «Пар» — по возможности короткие переводы: длинный раздувает плитку.
export const SHORT_TRANSLATION = 40;
export function pairsPool(cards) {
  const short = cards.filter((c) => String(c.translation).trim().length <= SHORT_TRANSLATION);
  return short.length >= 2 ? short : cards;
}

// Вопрос: перевод карточки → верное слово среди k вариантов. Варианты из того же набора,
// без повторов по написанию (у двух карточек бывает одно и то же слово).
export function makeQuestion(card, pool, k = 4, rnd = Math.random) {
  const answer = String(card.word).trim();
  const seen = new Set([answer.toLowerCase()]);
  const others = [];
  for (const c of shuffle(pool, rnd)) {
    const w = String(c.word || '').trim();
    if (!w || seen.has(w.toLowerCase())) continue;
    seen.add(w.toLowerCase());
    others.push(w);
    if (others.length >= k - 1) break;
  }
  const options = shuffle([answer, ...others], rnd);
  return { card, prompt: String(card.translation).trim(), answer, options };
}
